/**
 * Main Application with React Router
 *
 * Routes:
 * /                         - Landing page (public); signed-in/standalone-mode users
 *                             are sent straight to /dashboard/library (the platform's
 *                             landing page since retirement PR 1, 2026-10-01 -- see
 *                             AppRoutes, AdminGuard, DevModeAuth,
 *                             LandingPageWrapper.onGoToDashboard)
 * /sign-in                  - Clerk sign-in (public); afterSignInUrl also lands on
 *                             /dashboard/library
 * /sign-up                  - Clerk sign-up (public); new users go to /dashboard/onboarding
 *                             instead — a deliberately different, one-time flow
 * /sso-callback             - OAuth redirect handler (Clerk)
 * /dashboard                - Dashboard overview (protected, sidebar layout) — reachable via
 *                             the sidebar's "Dashboard" link, but not itself the landing page
 * /dashboard/backtest/new   - Redirects to /dashboard/library (deprecated standalone route)
 * /dashboard/jobs           - Legacy redirect to /dashboard/library (old bookmarks; formerly
 *                             /dashboard/research, retired below)
 * /dashboard/jobs/:jobId, /dashboard/backtests, /dashboard/workflows(/:id)
 *                           - Retired backtest-results surface; redirect to /dashboard/library
 * /dashboard/meta-portfolios - The one gate-enforced portfolio per tenant --
 *                              create/edit with an inline live blend preview
 *                              (formerly split across this page and a
 *                              separate Ensemble Lab route, retired 2026-09-06)
 * /dashboard/strategies     - Retired 2026-09-06, redirects to meta-portfolios
 *                             (single-strategy registry had zero real usage)
 * /dashboard/ensemble-lab   - Retired 2026-09-06, redirects to meta-portfolios
 * /dashboard/settings       - Settings
 * /dashboard/admin          - Admin panel
 * /dashboard/team           - Team management
 * /dashboard/onboarding     - Onboarding wizard
 * /dashboard/support        - Support tickets
 * /dashboard/deploy         - Strategy deployment
 * /dashboard/mandate        - The active trading mandate (real backend; see mandate/enabled.ts)
 * /dashboard/mandate/new    - Write, review and sign a mandate version
 * /dashboard/library        - Documented strategies (?for=active-mandate shows eligibility)
 * /dashboard/proposals      - The agent's proposals; /new asks for one, /latest shows the newest
 * /dashboard/account        - Small always-mocked "see it again" panel for the last paper
 *                              deployment -- not the full account monitor
 *
 * Mandate-first entry (always on; see mandate/enabled.ts): AppShell sends a
 * user with no active mandate to /dashboard/mandate/new once, on arrival at /dashboard (the
 * root), recorded per browser in localStorage (see mandate/wizardSeen.ts). Every other route
 * stays reachable without a mandate; mandate-dependent pages show an inline message instead.
 * Signing a mandate then auto-navigates to /dashboard/library?for=active-mandate (see
 * MandateWizardPage.tsx's onSign effect).
 *
 * Retired discovery/research surface (design council PR A, 2026-10-14): the alpha-discovery
 * workflow engine behind /dashboard/research/*, /dashboard/discover and /dashboard/strategy-audit
 * was deleted server-side in retirement PR 3 (#270). All three now redirect straight to
 * /dashboard/library instead of rendering a page whose backend is gone.
 */

import React, { Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import CustomSignIn from './auth/CustomSignIn';
import CustomSignUp from './auth/CustomSignUp';
import { TourProvider } from './components/tour';
import { ClerkProvider, useCurrentUser } from './auth';
import { LandingPage } from './landing';
import { AppShell } from './components/layout/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import { useSeo } from './hooks/useSeo';
import { isClerkConfigured, useMockApi } from './config';
import { normalizeTier, tierLabel } from './billing/tiers';
import { isMandateUiEnabled } from './mandate/enabled';
import { useActiveMandate } from './mandate/api';
import { useAppNavigate } from './hooks/useAppNavigate';
import { PageTransition } from './components/PageTransition';
import { LoadingScreen } from './components/brand/LoadingScreen';
import { trackPageView, trackSignUp, trackSignUpReason } from './lib/analytics';
import { useErrorTracking } from './errors';
import { Check, ArrowLeft, ArrowRight, Wrench } from 'lucide-react';

// ---------------------------------------------------------------------------
// Lazy-loaded page components (code-split per route)
// ---------------------------------------------------------------------------

const DashboardPageModule = React.lazy(() =>
  import('./dashboard').then((m) => ({ default: m.DashboardPage }))
);
const SettingsPage = React.lazy(() =>
  import('./settings').then((m) => ({ default: m.SettingsPage }))
);
const AdminPage = React.lazy(() =>
  import('./admin').then((m) => ({ default: m.AdminPage }))
);
const TeamPage = React.lazy(() =>
  import('./team').then((m) => ({ default: m.TeamPage }))
);
const OnboardingPageModule = React.lazy(() =>
  import('./onboarding').then((m) => ({ default: m.OnboardingPage }))
);
const TicketsPage = React.lazy(() =>
  import('./tickets').then((m) => ({ default: m.TicketsPage }))
);
const DocsPage = React.lazy(() =>
  import('./docs').then((m) => ({ default: m.DocsPage }))
);
const PricingPage = React.lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const FaqPage = React.lazy(() =>
  import('./pages/FaqPage').then((m) => ({ default: m.FaqPage }))
);
const DeploymentsPage = React.lazy(() => import('./pages/DeploymentsPage'));
const MetaPortfoliosPage = React.lazy(() => import('./pages/MetaPortfoliosPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const ExchangesPage = React.lazy(() => import('./pages/ExchangesPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const LineageSubtreePage = React.lazy(() => import('./pages/LineageSubtreePage'));
const MandatePage = React.lazy(() => import('./pages/MandatePage'));
const MandateWizardPage = React.lazy(() => import('./pages/MandateWizardPage'));
const LibraryPage = React.lazy(() => import('./pages/LibraryPage'));
const ProposalPage = React.lazy(() => import('./pages/ProposalPage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const RecordPage = React.lazy(() => import('./pages/RecordPage'));

const PrivacyPolicyPage = React.lazy(() =>
  import('./landing/pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy }))
);
const TermsOfServicePage = React.lazy(() =>
  import('./landing/pages/TermsOfService').then((m) => ({ default: m.TermsOfService }))
);

// ---------------------------------------------------------------------------
// Route-level loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return <LoadingScreen />;
}

// ============================================================================
// Auth Pages (Tailwind)
// ============================================================================

const SignInPage: React.FC = () => {
  const navigate = useNavigate();

  useSeo({
    title: 'Sign in | Mendl Labs',
    description: 'Sign in to your Mendl Labs dashboard to review backtests, strategies, and live execution workflows.',
    canonicalPath: '/sign-in',
    noindex: true,
  });

  return (
    <div className="flex min-h-screen bg-background">
      {/* Trust panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center bg-[hsl(225,20%,4%)] px-16">
        <img src="/logo.svg" alt="MendlLabs" className="mb-10 h-8 w-auto self-start" />
        <h1 className="font-heading text-3xl font-bold text-white mb-4 leading-tight">
          Welcome back
        </h1>
        <p className="text-white/50 text-lg mb-8">
          Sign in to your dashboard and pick up where you left off.
        </p>
        <ul className="space-y-4 text-white/60 text-sm">
          <li className="flex items-center gap-3"><Check size={14} className="shrink-0 text-[#2dd4a0]" /><span>Paper account only. No real money is traded today</span></li>
          <li className="flex items-center gap-3"><Check size={14} className="shrink-0 text-[#2dd4a0]" /><span>You set the limits; code enforces them</span></li>
          <li className="flex items-center gap-3"><Check size={14} className="shrink-0 text-[#2dd4a0]" /><span>The AI proposes; you approve or reject</span></li>
          <li className="flex items-center gap-3"><Check size={14} className="shrink-0 text-[#2dd4a0]" /><span>Live trading is not available yet</span></li>
        </ul>
      </div>

      {/* Auth form */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center p-5 relative">
        <button
          onClick={() => navigate('/')}
          className="absolute left-5 top-5 rounded-md border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={14} className="mr-1" />Back to Home
        </button>
        {isClerkConfigured() ? (
          <CustomSignIn />
        ) : (
          <DevModeAuth onContinue={() => navigate('/dashboard/library')} />
        )}
      </div>
    </div>
  );
};

/**
 * Marketing ruling M1 (2026-10-20), kill criterion K-M1: ask every signup
 * what brought them here. The answer is a kill-criteria input (50%+ of the
 * first 20 answering "bring my own strategy" or "an AI that finds trades"
 * switches the headline), so it is recorded as an analytics event and in
 * sessionStorage for onboarding to pick up -- it never blocks sign-up.
 */
const SIGN_UP_REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'see_rules_and_limits_first', label: 'I want to see the rules and the limits before anything moves' },
  { value: 'would_sell_in_a_fall', label: 'I would sell in a big fall, and I know it' },
  { value: 'run_money_for_others', label: 'I run money for other people' },
  { value: 'bring_own_strategy', label: 'I want to bring my own strategy or code' },
  { value: 'ai_finds_trades', label: 'I want an AI that finds winning trades' },
  { value: 'other', label: 'Something else' },
];

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier');
  const selectedTier = normalizeTier(tier);
  const selectedTierLabel = tierLabel(selectedTier);
  const isObserverTier = selectedTier === 'observer';
  const [signUpReason, setSignUpReason] = React.useState<string>(
    () => sessionStorage.getItem('sign_up_reason') ?? '',
  );

  const signUpTitle = isObserverTier ? 'Start with Observer' : `Start with ${selectedTierLabel}`;
  const signUpDescription = isObserverTier
    ? 'Create a free Observer account. It starts on a paper account, with no real money. Live trading is not available yet.'
    : `${selectedTierLabel} is a paid plan. Create your account first, then confirm billing inside the product.`;
  const signUpBullets = isObserverTier
    ? [
        'No credit card required to start',
        'Paper account only. No real money is traded today',
        'You set the limits; code enforces them',
        'The AI proposes; you approve or reject',
      ]
    : [
        'Create your account first',
        `Confirm ${selectedTierLabel} billing inside the product`,
        'Every account starts on paper',
        'Change plan any time inside the product',
      ];

  useSeo({
    title: 'Sign up | Mendl Labs',
    description: 'Create a Mendl Labs account and open a paper account. No real money is traded today.',
    canonicalPath: '/sign-up',
    noindex: true,
  });

  const onReasonChange = (value: string) => {
    setSignUpReason(value);
    if (value) {
      sessionStorage.setItem('sign_up_reason', value);
      trackSignUpReason(value);
    } else {
      sessionStorage.removeItem('sign_up_reason');
    }
  };

  React.useEffect(() => {
    if (tier) {
      sessionStorage.setItem('intended_tier', selectedTier);
    }
    trackSignUp(tier ?? undefined);
  }, [tier, selectedTier]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Trust panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center bg-[hsl(225,20%,4%)] px-16">
        <img src="/logo.svg" alt="MendlLabs" className="mb-10 h-8 w-auto self-start" />
        <h1 className="font-heading text-3xl font-bold text-white mb-4 leading-tight">
          {signUpTitle}
        </h1>
        <p className="text-white/50 text-lg mb-8">
          {signUpDescription}
        </p>
        <ul className="space-y-4 text-white/60 text-sm">
          {signUpBullets.map((item) => (
            <li key={item} className="flex items-center gap-3"><Check size={14} className="shrink-0 text-[#2dd4a0]" /><span>{item}</span></li>
          ))}
        </ul>
      </div>

      {/* Auth form */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center p-5 relative">
        <button
          onClick={() => navigate('/')}
          className="absolute left-5 top-5 rounded-md border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={14} className="mr-1" />Back to Home
        </button>
        <div className="mb-5 w-full max-w-[400px]">
          <label htmlFor="sign-up-reason" className="block text-xs font-medium text-muted-foreground mb-1.5">
            What brought you here? (optional)
          </label>
          <select
            id="sign-up-reason"
            value={signUpReason}
            onChange={(e) => onReasonChange(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">Choose one</option>
            {SIGN_UP_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        {isClerkConfigured() ? (
          <CustomSignUp selectedTierLabel={selectedTierLabel} isFreeTier={isObserverTier} />
        ) : (
          <DevModeAuth onContinue={() => navigate('/dashboard/onboarding')} />
        )}
      </div>
    </div>
  );
};

const DevModeAuth: React.FC<{ onContinue: () => void }> = ({ onContinue }) => (
  <div className="max-w-[400px] rounded-lg bg-card p-10 text-center">
    <div className="mb-4 flex justify-center"><Wrench size={40} className="text-muted-foreground" /></div>
    <h2 className="mb-3 text-lg font-semibold text-foreground">Dev Mode</h2>
    <p className="mb-6 leading-relaxed text-muted-foreground">
      Clerk authentication is not configured. You&apos;ll be signed in as a dev
      user.
    </p>
    <button
      onClick={onContinue}
      className="rounded-lg bg-primary px-6 py-3 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
    >
      <span className="inline-flex items-center gap-1.5">Continue to Dashboard <ArrowRight size={14} /></span>
    </button>
  </div>
);

// ============================================================================
// Landing Page Wrapper
// ============================================================================

const LandingPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useCurrentUser();
  const mandateUiOn = isMandateUiEnabled();
  // Only fires for a signed-in visitor (the Dashboard button Nav renders is
  // itself gated on isSignedIn) -- an anonymous landing-page view never
  // queries this.
  const activeMandateQuery = useActiveMandate(mandateUiOn && isLoaded && isSignedIn);

  // The "Dashboard" button is this environment's post-login landing (see
  // this file's header comment): normally straight to /dashboard/library.
  // But AppShell's mandate-first guard deliberately excludes
  // /dashboard/library from its no-mandate redirect (library must stay
  // directly reachable for a user who already has a mandate, e.g. the
  // storyboard spec's direct page.gotos) -- so a genuinely new user with no
  // mandate yet who lands there via THIS button would skip the mandate
  // wizard entirely. Resolve the mandate here, at the one entry point that
  // needs to choose, rather than widening that guard's exclusion list.
  // `refetch` (not the possibly-still-loading hook state) so the very first
  // click after sign-up doesn't race the query. Fails open to library on
  // any error, matching the guard's own fail-open fallback.
  const goToDashboard = async () => {
    if (mandateUiOn) {
      try {
        const { data } = await activeMandateQuery.refetch({ throwOnError: true });
        if (data && data.mandate == null) {
          navigate('/dashboard/mandate/new');
          return;
        }
      } catch {
        // fall through to the normal library landing.
      }
    }
    navigate('/dashboard/library');
  };

  return (
    <LandingPage
      onSignIn={() => navigate('/sign-in')}
      onSignUp={(tier) => navigate(tier ? `/sign-up?tier=${tier}` : '/sign-up')}
      onGoToDashboard={goToDashboard}
      isSignedIn={isLoaded && isSignedIn}
    />
  );
};

// ============================================================================
// Route Wrappers — inject router navigation into legacy prop-based components
// ============================================================================

const DashboardView: React.FC = () => {
  const nav = useAppNavigate();
  return (
    <DashboardPageModule
      onNavigate={nav.navigateToView}
      onViewBacktest={nav.navigateToResults}
    />
  );
};

const OnboardingView: React.FC = () => {
  const nav = useAppNavigate();
  return (
    <OnboardingPageModule
      onComplete={nav.navigateToDashboard}
      onNavigate={nav.navigateToView}
    />
  );
};

// ============================================================================
// Admin Route Guard
// ============================================================================

const AdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, isLoaded } = useCurrentUser();
  if (!isLoaded) return <PageLoader />;
  if (role !== 'admin' && role !== 'owner') {
    return <Navigate to="/dashboard/library" replace />;
  }
  return <>{children}</>;
};

// ============================================================================
// App Routes
// ============================================================================

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const showPublicLanding = isClerkConfigured() || useMockApi();

  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
    <Routes location={location} key={location.pathname}>
      {/* Public routes. Post-login landing is /dashboard/library since
          retirement PR 1 (council ruling R27, 2026-10-01): the research /
          alpha-discovery surface was retired and deleted. */}
      <Route path="/" element={
        showPublicLanding
          ? <LandingPageWrapper />
          : <Navigate to="/dashboard/library" replace />
      } />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback afterSignInUrl="/dashboard/library" afterSignUpUrl="/dashboard/onboarding" />} />
      <Route path="/docs" element={<Suspense fallback={<PageLoader />}><PageTransition><DocsPage /></PageTransition></Suspense>} />
      <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><PageTransition><PricingPage /></PageTransition></Suspense>} />
      <Route path="/faq" element={<Suspense fallback={<PageLoader />}><PageTransition><FaqPage /></PageTransition></Suspense>} />
      {/* Retired (marketing ruling M1, 2026-10-20, delete list item 23): the
          public sample-backtest walkthrough was a mock BTC backtest from the
          retired backtesting product. Redirect like /sample-interview below. */}
      <Route path="/sample-backtest" element={<Navigate to="/" replace />} />
      {/* Retired: the sample research-interview page (the interview surface was removed with alpha-discovery). */}
      <Route path="/sample-interview" element={<Navigate to="/" replace />} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PageTransition><PrivacyPolicyPage /></PageTransition></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<PageLoader />}><PageTransition><TermsOfServicePage /></PageTransition></Suspense>} />

      {/* Protected dashboard — nested routes inside AppShell layout */}
      <Route path="/dashboard" element={<AppShell />}>
        <Route index element={<Suspense fallback={<PageLoader />}><PageTransition><DashboardView /></PageTransition></Suspense>} />
        {/* Retired discovery/research surface (design council PR A,
            2026-10-14): the alpha-discovery workflow engine behind all of
            these was deleted server-side in retirement PR 3 (#270). Redirect
            rather than render a page whose backend is gone, or leave it
            reachable-but-silently-broken. */}
        <Route path="discover" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="backtest/new" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="research/*" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="strategy-audit" element={<Navigate to="/dashboard/library" replace />} />
        {/* Legacy redirects — preserve old bookmark URLs (formerly routed
            through /dashboard/research, now retired above) */}
        <Route path="lab" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="jobs" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="jobs/:jobId" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="lineage/:rootId" element={<Suspense fallback={<PageLoader />}><PageTransition><LineageSubtreePage /></PageTransition></Suspense>} />
        {/* Retired 2026-09-06 (quant council + page-consolidation ruling):
            single-strategy registry/deployment had zero real usage
            (strategy_instances/deployed_strategies both empty in
            production) and the platform now deploys via one gate-enforced
            portfolio per tenant instead -- see Meta-Portfolios. */}
        <Route path="strategies" element={<Navigate to="/dashboard/meta-portfolios" replace />} />
        {/* Retired backtest-results surface (stage 1 of the backtest_results
            retirement): the per-job results page and the backtest jobs list
            are no longer reachable from the GUI. Redirect rather than 404 so
            bookmarks and old deep links (including /:id forms) land on the
            library. The backend endpoints stay alive for Meta-Portfolios and
            deployments. */}
        <Route path="backtests" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="workflows" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="workflows/:workflowId" element={<Navigate to="/dashboard/library" replace />} />
        <Route path="meta-portfolios" element={<Suspense fallback={<PageLoader />}><PageTransition><MetaPortfoliosPage /></PageTransition></Suspense>} />
        {/* Retired 2026-09-06: Ensemble Lab's live-preview UI (ScreenPanel/
            BuildPanel) now lives inline in Meta-Portfolios' own create/edit
            flow -- no reason to visit a separate page just to preview a
            blend before committing to it. */}
        <Route path="ensemble-lab" element={<Navigate to="/dashboard/meta-portfolios" replace />} />
        <Route path="docs" element={<Suspense fallback={<PageLoader />}><PageTransition><DocsPage /></PageTransition></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoader />}><PageTransition><SettingsPage /></PageTransition></Suspense>} />
        <Route path="exchanges" element={<Suspense fallback={<PageLoader />}><PageTransition><ExchangesPage /></PageTransition></Suspense>} />
        {/* Onboarding is registered unconditionally -- unlike the SaaS-only
            routes below, it must work in dev/mock mode too (DevModeAuth's
            sign-up path lands here), otherwise the mock environment silently
            diverges from production and this exact gap goes unnoticed again.
            useOnboarding.ts gets its auth token via the app's dev-mode-safe
            useAuthToken() wrapper, not raw Clerk hooks, so this is safe to
            render without a ClerkProvider in scope. */}
        <Route path="onboarding" element={<Suspense fallback={<PageLoader />}><PageTransition><OnboardingView /></PageTransition></Suspense>} />
        {/* R58 Record: the account history and its CSV export. Reads /api/record, which exists server-side, so it is
            not behind the mandate flag. The sidebar entry is added by the navigation (group 6) follow-up. */}
        <Route path="record" element={<Suspense fallback={<PageLoader />}><PageTransition><RecordPage /></PageTransition></Suspense>} />
        {/* Mandate UI: registered only when enabled (always in mock mode, else the
            ff_mandate_v1 flag) because its API does not exist server-side yet. */}
        {isMandateUiEnabled() && (
          <>
            <Route path="mandate" element={<Suspense fallback={<PageLoader />}><PageTransition><MandatePage /></PageTransition></Suspense>} />
            <Route path="mandate/new" element={<Suspense fallback={<PageLoader />}><PageTransition><MandateWizardPage /></PageTransition></Suspense>} />
            <Route path="library" element={<Suspense fallback={<PageLoader />}><PageTransition><LibraryPage /></PageTransition></Suspense>} />
            <Route path="proposals" element={<Suspense fallback={<PageLoader />}><PageTransition><ProposalPage /></PageTransition></Suspense>} />
            <Route path="proposals/:proposalId" element={<Suspense fallback={<PageLoader />}><PageTransition><ProposalPage /></PageTransition></Suspense>} />
            {/* Small, always-mocked "see it again" panel for whatever proposal
                was last approved -- deliberately not the full WP5.3 account
                monitor (see AccountPage.tsx's header comment). */}
            <Route path="account" element={<Suspense fallback={<PageLoader />}><PageTransition><AccountPage /></PageTransition></Suspense>} />
          </>
        )}
        {/* SaaS-only routes: admin, team, support, deploy, billing */}
        {isClerkConfigured() && (
          <>
            <Route path="admin" element={<AdminGuard><Suspense fallback={<PageLoader />}><PageTransition><AdminPage /></PageTransition></Suspense></AdminGuard>} />
            <Route path="team" element={<Suspense fallback={<PageLoader />}><PageTransition><TeamPage /></PageTransition></Suspense>} />
            <Route path="support" element={<Suspense fallback={<PageLoader />}><PageTransition><TicketsPage /></PageTransition></Suspense>} />
            <Route path="deploy" element={<Suspense fallback={<PageLoader />}><PageTransition><DeploymentsPage /></PageTransition></Suspense>} />
            <Route path="billing" element={<Suspense fallback={<PageLoader />}><PageTransition><BillingPage /></PageTransition></Suspense>} />
          </>
        )}
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
    </Routes>
    </AnimatePresence>
  );
};

// ============================================================================
// App Root
// ============================================================================

const App: React.FC = () => {
  useErrorTracking({
    environment: import.meta.env.MODE,
    sampleRate: 1.0,
    maxBreadcrumbs: 50,
  });

  return (
    <ErrorBoundary>
      <ClerkProvider>
        <BrowserRouter>
          <TourProvider>
            <AppRoutes />
          </TourProvider>
        </BrowserRouter>
      </ClerkProvider>
    </ErrorBoundary>
  );
};

export default App;
